import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// IdleLogout — wraps the entire <Routes> tree (mounted ONCE at the top
// of App.js) so the inactivity timer survives every page navigation.
// Modelled on the TotalCare hospital app: after `timeout` ms with no
// user activity (mousemove, click, keypress, touch) we clear the auth
// state and bounce to /login. That kills any live API client in this
// tab and lets the backend pool reclaim the connection.
//
// The timer runs unconditionally. Even an idle login screen restarts
// the timer on activity — cheap to do, removes the risk of the timer
// silently being unarmed if the user object reference flickers.
export default function IdleLogout({ timeout = 10 * 60 * 1000, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const timer    = useRef(null);
  const locRef   = useRef(location);
  locRef.current = location;

  const resetTimer = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // Don't bother bouncing if they're already on the login screen.
      const path = locRef.current?.pathname || '';
      if (path === '/login' || path === '/') return;

      // Clear every key the app uses to track auth so AuthGuard fails
      // closed on the next render.
      try {
        localStorage.removeItem('school_token');
        localStorage.removeItem('school_user');
        sessionStorage.clear();
      } catch { /* ignore */ }

      navigate('/login', { replace: true });
      // Force a hard reload after the redirect so any axios interceptors,
      // websockets, or in-memory caches drop their state too.
      setTimeout(() => { try { window.location.reload(); } catch {} }, 50);
    }, timeout);
  };

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, resetTimer));
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
}
